import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Card, Col, Input, Pagination, Row, Space, Spin, Typography } from 'antd';
import { ArrowRightOutlined, CalendarOutlined, FileTextOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PagesWrapper from '../../../PagesWrapper';
import * as newsService from '../../../services/newsService';
import { getFileUrl } from '../../../utils/urlUtils';
import { normalizeLanguageCode } from '../../../utils/languagePreference';
import styles from './NewsPublic.module.css';

const { Search } = Input;
const { Title, Paragraph, Text } = Typography;

const getAuthorName = (item, t) => item.addedByName || t('news.editorialTeam', 'HeatCraftHP editorial team');
const getActiveLanguageCode = (i18n) => normalizeLanguageCode(i18n.resolvedLanguage || i18n.language) || 'en';

const NewsroomStudioMark = () => (
  <svg viewBox="0 0 360 132" className={styles.heroMark} role="img" aria-label="HeatCraftHP Newsroom">
    <rect x="14" y="20" width="332" height="94" rx="24" fill="#081b44" />
    <rect x="26" y="32" width="308" height="70" rx="18" fill="#0d2e72" />
    <path d="M66 14h228" stroke="#7fc4ff" strokeWidth="4" strokeLinecap="round" opacity="0.55" />
    <path d="M112 14v14" stroke="#7fc4ff" strokeWidth="4" strokeLinecap="round" opacity="0.55" />
    <path d="M248 14v14" stroke="#7fc4ff" strokeWidth="4" strokeLinecap="round" opacity="0.55" />
    <path d="M89 24h182" stroke="#7fe0ff" strokeWidth="12" strokeLinecap="round" opacity="0.12" />
    <rect x="44" y="48" width="56" height="8" rx="4" fill="#8ed8ff" opacity="0.9" />
    <rect x="44" y="64" width="36" height="8" rx="4" fill="#8ed8ff" opacity="0.45" />
    <rect x="260" y="48" width="56" height="8" rx="4" fill="#8ed8ff" opacity="0.9" />
    <rect x="280" y="64" width="36" height="8" rx="4" fill="#8ed8ff" opacity="0.45" />
    <text x="118" y="60" fill="#dff4ff" fontSize="23" fontWeight="700" letterSpacing="1.2">HeatCraftHP</text>
    <text x="118" y="86" fill="#7fe0ff" fontSize="28" fontWeight="800" letterSpacing="2.8">NEWSROOM</text>
    <rect x="120" y="92" width="116" height="4" rx="2" fill="#1fb6ff" opacity="0.8" />
  </svg>
);

/**
 * Public News landing page with language-aware cards and professional editorial layout.
 */
const NewsPublic = () => {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 9, total: 0 });

  const languageCode = getActiveLanguageCode(i18n);

  const fetchNews = useCallback(async (page = 1, searchText = search) => {
    setLoading(true);
    try {
      const result = await newsService.getPublicNews({
        page,
        pageSize: pagination.pageSize,
        languageCode,
        search: searchText || undefined,
      }, { suppressNotification: true });
      setItems(result.items || []);
      setPagination(prev => ({ ...prev, current: result.page, total: result.totalCount }));
    } catch (error) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [languageCode, pagination.pageSize, search]);

  const handleSearch = useCallback((value) => {
    const nextValue = value ?? '';
    setSearch(nextValue);
    fetchNews(1, nextValue);
  }, [fetchNews]);

  useEffect(() => {
    fetchNews(1, search);
    // Refetch when the active UI language changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageCode]);

  const renderMeta = (item) => (
    <Space wrap size={[12, 6]} className={styles.meta}>
      <span><CalendarOutlined /> {item.publishedAt ? dayjs(item.publishedAt).format('DD MMM YYYY') : dayjs(item.dateModified).format('DD MMM YYYY')}</span>
      <span>{t('news.readingTime', '{{count}} min read', { count: item.readingTimeMinutes || 1 })}</span>
    </Space>
  );

  return (
    <PagesWrapper>
      <header className={styles.hero}>
        <div className={styles.heroContent}>
          <div className={styles.heroShell}>
            <div className={styles.heroLead}>
              <NewsroomStudioMark />
              <Title level={1} className={styles.heroTitle}>{t('news.publicTitle', 'Latest project news and updates')}</Title>
            </div>

            <aside className={styles.heroPanel}>
              <div className={styles.searchWrapper}>
                <Text className={styles.searchLabel}>{t('news.findStory', 'Find a story')}</Text>
                <Search
                  allowClear
                  size="middle"
                  value={search}
                  placeholder={t('news.publicSearchPlaceholder', 'Search news...')}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSearch(value);
                    if (!value) {
                      fetchNews(1, '');
                    }
                  }}
                  onSearch={handleSearch}
                />
              </div>
            </aside>
          </div>
        </div>
      </header>

      <main className={styles.container}>
        {loading ? (
          <div className={styles.loading}><Spin size="large" /></div>
        ) : items.length === 0 ? (
          <div className={styles.emptyState}>{t('news.noPublicNews', 'No news for now...')}</div>
        ) : (
          <>
            <div className={styles.list}>
              {items.map(item => (
                <Card key={item.id} className={styles.listCard} bodyStyle={{ padding: 0 }}>
                  <Row gutter={0} align="stretch">
                    <Col xs={24} md={8} lg={7} xl={6}>
                      <Link to={newsService.getNewsPublicPath(item)} className={styles.mediaLink} aria-label={item.title}>
                        <div className={styles.listImageWrap}>
                          {item.coverImageUrl ? (
                            <img src={getFileUrl(item.coverImageUrl)} alt={item.title} className={styles.listImage} />
                          ) : (
                            <div className={styles.listPlaceholder}><FileTextOutlined /></div>
                          )}
                        </div>
                      </Link>
                    </Col>
                    <Col xs={24} md={16} lg={17} xl={18}>
                      <div className={styles.listContent}>
                        <div className={styles.listEyebrow}>{item.countryLanguage || item.countryName}</div>
                        <Link to={newsService.getNewsPublicPath(item)} className={styles.titleLink}>
                          <Title level={3} className={styles.listTitle}>{item.title}</Title>
                        </Link>
                        {renderMeta(item)}
                        <Paragraph className={styles.listExcerpt}>{item.excerpt}</Paragraph>

                        <div className={styles.listFooter}>
                          <Space className={styles.authorLine}>
                            <Avatar src={getFileUrl(item.addedByProfileImageUrl)} icon={<UserOutlined />} />
                            <Text>{t('news.byAuthor', 'By {{name}}', { name: getAuthorName(item, t) })}</Text>
                          </Space>
                          <Link to={newsService.getNewsPublicPath(item)} className={styles.cardReadLink}>
                            <span className={styles.readMoreLink}>
                              {t('news.readArticle', 'Read article')}
                              <ArrowRightOutlined />
                            </span>
                          </Link>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              ))}
            </div>

            <div className={styles.pagination}>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={(page) => fetchNews(page)}
                showSizeChanger={false}
              />
            </div>
          </>
        )}
      </main>
    </PagesWrapper>
  );
};

export default NewsPublic;
